import {organizationApiClient} from 'organization/organizationApiClient'
import React, {useEffect, useState} from 'react'
import {useRouteOrganizationEventId} from 'utils/lib/url'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import {toast} from 'react-toastify'

export type CsvImport = {
  id: number
  file: string
  status: string
  message: string
  total: number
  imported: number
  fails: number
  created_at: Date
}

export interface CsvImportContextProps {
  imports: CsvImport[]
  startImport: (
    fileName: string,
    emails: string[],
    reservation: boolean,
  ) => Promise<string | boolean>
  loading: boolean
}

export const CsvImportContext = React.createContext<
  CsvImportContextProps | undefined
>(undefined)

export default function CsvImportProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [loading, setLoading] = useState<boolean>(true)
  const [imports, setImports] = useState<CsvImport[]>([])
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    setLoading(true)

    await apiClient
      .get(`api/event/${eventId}/imports`)
      .then((response) => {
        setImports(response)
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const startImport = async (
    fileName: string,
    emails: string[],
    reservation: boolean,
  ) => {
    setLoading(true)

    const result = await apiClient
      .post(`api/event/${eventId}/imports`, {
        file_name: fileName,
        emails: emails,
        reservation: reservation,
      })
      .then(() => get())
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    return result
  }

  return (
    <CsvImportContext.Provider
      value={{
        imports,
        startImport,
        loading,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && <>{props.children}</>}
    </CsvImportContext.Provider>
  )
}

export function useCsvImport() {
  const context = React.useContext(CsvImportContext)
  if (context === undefined) {
    throw new Error('useCsvImport must be used within CsvImportProvider')
  }
  return context
}
